import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { debug } from '@/shared/lib';
import { microphoneController } from '@/shared/lib/microphone/microphoneController';
import { toastCaller } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import {
  getTranscriptorErrorMessage,
  TRANSCRIPTOR_LANGUAGE,
  trackRecordingEvent,
  transcriptorModel,
} from '@/entities/transcriptor';

import { transcriptorRecordingMessages } from '../config/i18n';
import { MicrophoneDevice, RecordingMode } from '../config/types';
import { liveSession } from '../model';
import { audioRecordingService } from '../services';

const {
  resetRecording,
  setConsultationData,
  startRecording: startRecordingAction,
} = transcriptorModel.actions;
const { startRecordingThunk, fetchConsultationById, fetchConsultations } =
  transcriptorModel.thunks;

type UseStartRecordingParams = {
  patientID: string | undefined;
  schemaId: string | undefined;
  selectedOnDemandSeriesIds?: string[];
  language: TRANSCRIPTOR_LANGUAGE;
  selectedDevice: string;
  devices: MicrophoneDevice[];
  encounterId?: string;
  encounterKind?: 'consultation' | 'treatment';
  recordingMode?: RecordingMode;
  // The organization has Transcriptor v2; without it, a start is the one
  // request it was before.
  transcriptorV2?: boolean;
  closeModalId?: ModalID | null;
  // The stream is the one being recorded; live features may read it in parallel.
  onStarted?: (
    consultationId: string,
    stream: MediaStream,
    accepted: { recordingMode: RecordingMode; transcriptorV2: boolean },
  ) => void;
  onCleanup?: () => void;
};

type UseStartRecordingReturn = {
  startRecording: () => Promise<void>;
  isStarting: boolean;
  error: string | null;
};

export const useStartRecording = ({
  patientID,
  schemaId,
  selectedOnDemandSeriesIds,
  language,
  selectedDevice,
  devices,
  encounterId,
  encounterKind,
  recordingMode,
  transcriptorV2 = false,
  closeModalId = null,
  onCleanup,
  onStarted,
}: UseStartRecordingParams): UseStartRecordingReturn => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const startRecording = useCallback(async () => {
    if (onCleanup) {
      onCleanup();
    }

    // Get microphone name
    const microphoneName =
      devices.find((device) => device.deviceId === selectedDevice)?.label ||
      formatMessage(transcriptorRecordingMessages.defaultMicrophone);

    const result = await dispatch(
      startRecordingThunk({
        schemaId,
        selectedOnDemandSeriesIds,
        language,
        encounterId,
        patientId: patientID,
        recordingMode,
        transcriptorV2,
      }),
    )
      .unwrap()
      .catch((error: unknown) => {
        const refusal = getTranscriptorErrorMessage(error);

        toastCaller({
          type: 'error',
          heading: formatMessage(toastErrors.errorHeading),
          message: refusal
            ? formatMessage(refusal)
            : formatMessage(toastErrors.defaultError),
        });

        return null;
      });

    if (!result) {
      return;
    }

    try {
      dispatch(
        setConsultationData({
          consultationId: result.consultationId,
          fragmentId: result.fragmentId,
        }),
      );

      const stream =
        microphoneController.getStream() ||
        (await microphoneController.open(
          selectedDevice ? { deviceId: selectedDevice } : {},
        ));

      const recordingContext = {
        encounter_id: result.consultationId,
        fragment_id: result.fragmentId,
      };

      // Dictation Stop waits for live answers. The server then renders them.
      const finishDictation = () => {
        dispatch(resetRecording());
        dispatch(
          transcriptorModel.actions.setConsultationStatus({
            consultationId: result.consultationId,
            status: 'processing',
          }),
        );
        void dispatch(
          fetchConsultationById({
            consultationId: result.consultationId,
            patientId: patientID,
          }),
        );
      };

      audioRecordingService.setCallbacks({
        onUploadIssue: (hasIssue) => {
          debug('Audio upload issue:', hasIssue);
        },
        onError: (error) => {
          debug('Audio recording error:', error);
          trackRecordingEvent('upload_failed', { ...recordingContext, error });
          if (!result.transcriptorV2) {
            dispatch(transcriptorModel.actions.stopRecording());
            return;
          }
          const refusal = getTranscriptorErrorMessage(error);
          toastCaller({
            type: 'error',
            message: formatMessage(refusal ?? toastErrors.defaultError),
          });
          audioRecordingService.kill();
          if (result.recordingMode === 'dictation') {
            liveSession.clear();
          }
          dispatch(transcriptorModel.actions.setInterrupted());
        },
        onStopped: (stopReason) => {
          debug('Recording stopped and all chunks uploaded');
          trackRecordingEvent('stopped', {
            ...recordingContext,
            stop_reason: stopReason,
          });
          if (result.recordingMode === 'dictation') {
            finishDictation();
            return;
          }
          dispatch(transcriptorModel.actions.stopRecording());

          if (encounterId) {
            // Skip the list refresh because its stale status would overwrite the optimistic processing state.
            dispatch(
              transcriptorModel.actions.setConsultationStatus({
                consultationId: encounterId,
                status: 'processing',
              }),
            );
            return;
          }

          if (patientID) {
            dispatch(fetchConsultations({ patientId: patientID }));
          }
        },
        onAudioIssue: (issue) => {
          debug('Audio issue state changed:', issue);
          trackRecordingEvent('audio_issue', {
            ...recordingContext,
            reason: issue ?? 'cleared',
          });
          dispatch(transcriptorModel.actions.setAudioIssue(issue));
        },
        onChunkUploaded: (uploadedAt, chunkIndex) => {
          trackRecordingEvent('chunk_uploaded', {
            ...recordingContext,
            chunk_index: chunkIndex,
          });
          dispatch(transcriptorModel.actions.updateLastSavedAt(uploadedAt));
        },
        onDiagnostic: ({ name, attributes }) => {
          trackRecordingEvent(name, { ...recordingContext, ...attributes });
        },
        // The live transcript reaches the server before the recording stops.
        beforeStop: async () => {
          if (!result.transcriptorV2 || result.recordingMode !== 'dictation') {
            return;
          }
          return await liveSession.stop();
        },
      });

      await audioRecordingService.initialize(stream);
      await audioRecordingService.startRecording(
        result.consultationId,
        result.fragmentId,
      );
      trackRecordingEvent('started', {
        ...recordingContext,
        microphone: microphoneName,
      });

      dispatch(
        startRecordingAction({
          microphoneName:
            process.env.NODE_ENV === 'development' && !selectedDevice
              ? formatMessage(transcriptorRecordingMessages.testMicrophone)
              : microphoneName,
          consultationType: schemaId ?? '',
          encounterKind,
          language,
          patientId: patientID || '',
        }),
      );

      onStarted?.(result.consultationId, stream, result);
      if (result.transcriptorV2 && result.recordingMode === 'dictation') {
        liveSession.setDocuments(result.protocolDocumentIds);
      }

      if (patientID && !encounterId) {
        dispatch(fetchConsultations({ patientId: patientID }));
      }
    } catch (error) {
      debug('Failed to start recording after consultation created:', error);
      if (result.transcriptorV2) {
        toastCaller({
          type: 'error',
          message: formatMessage(toastErrors.defaultError),
        });
      }
    } finally {
      if (closeModalId) {
        dispatch(modalModel.actions.closeModal(closeModalId));
      }
    }
  }, [
    onCleanup,
    devices,
    selectedDevice,
    schemaId,
    selectedOnDemandSeriesIds,
    language,
    patientID,
    encounterId,
    encounterKind,
    recordingMode,
    transcriptorV2,
    closeModalId,
    onStarted,
  ]);

  return {
    startRecording,
    isStarting: false,
    error: null,
  };
};
